import type { Plan, Subscription } from '@base/api';
import { useSubscriptions } from './useSubscriptions';
import { useTeeAgentPlan } from './useTeeAgentPlan';
import { useStandardAgentPlan } from './useStandardAgentPlan';

export type GetBillingReturn =
  | {
      renewalDate: Date;
      activePlans: number;
      planTotalPrice: number;
      subscriptionId: string;
    }
  | undefined;

export const getBilling = (
  subscriptions: Subscription[],
  plan?: Plan,
): GetBillingReturn => {
  if (!plan) {
    return;
  }

  const aiAgentSubscription = subscriptions
    .filter((sub) => ['active', 'trialing'].includes(sub.status.toLowerCase()))
    .find((sub) => sub.productId === plan?.id);

  if (!aiAgentSubscription) {
    return;
  }

  const aiAgentSubscriptionItem = aiAgentSubscription.items.find(
    (sub) => sub.productId === plan?.id,
  );

  if (!aiAgentSubscriptionItem) {
    return;
  }

  const activePlans = aiAgentSubscriptionItem.quantity;
  const planUnitPrice = aiAgentSubscriptionItem.unitAmount / 100;

  return {
    renewalDate: new Date(aiAgentSubscription.periodStartDate),
    activePlans,
    planTotalPrice: planUnitPrice * activePlans,
    subscriptionId: aiAgentSubscription.id,
  };
};

export const useBilling = () => {
  const {
    data: teePlan,
    isPending: isTeePlanPending,
    refetch: refetchTeePlan,
  } = useTeeAgentPlan();

  const {
    data: plan,
    isPending: isPlanPending,
    refetch: refetchPlan,
  } = useStandardAgentPlan();

  const {
    data: subscriptions = [],
    isPending: isSubscriptionsPending,
    refetch: refetchSubscriptions,
  } = useSubscriptions();

  return {
    tee: getBilling(subscriptions, teePlan),
    standard: getBilling(subscriptions, plan),
    isPending: isTeePlanPending || isPlanPending || isSubscriptionsPending,
    refetch: async () => {
      const [teePlanResult, planResult, subscriptionsResult] =
        await Promise.all([
          refetchTeePlan(),
          refetchPlan(),
          refetchSubscriptions(),
        ]);

      return {
        tee: getBilling(subscriptionsResult.data ?? [], teePlanResult.data),
        standard: getBilling(subscriptionsResult.data ?? [], planResult.data),
      };
    },
  };
};
